import {
  Account,
  APIError,
  APIErrorCode,
  APIParameters,
  APIResult,
  Note,
  RoleType,
  SessionToken,
  SocketEventMap,
  SocketEventType,
} from "common";
import { BehaviorSubject, from, map, Observable } from "rxjs";
import { io, Socket } from "socket.io-client";
import logo from "../assets/logo.png";

/*
 * An end point call
 */
export type APICall<T extends keyof APIParameters> = (
  params: APIParameters[T]
) => Promise<APIResult[T]>;

export class API {
  /*
   * The current account
   */
  account$ = new BehaviorSubject<Account | undefined>(undefined);

  // Is logged in
  isLoggedIn$ = new BehaviorSubject(!!this.getSessionToken());

  notificationCount$ = new BehaviorSubject(0);
  notificationPermission$ = new BehaviorSubject<
    "default" | "granted" | "denied" | null
  >(null);

  private socket: Socket | null = null;
  private newNoteHandler: ((note: Note) => void) | null = null;

  private mePromises: ((params: APIResult["me"]) => void)[] | null = null;

  /*
   * Returns if the current account is in role
   */
  isInRole(roleType: RoleType): Observable<boolean> {
    return from(this.me()).pipe(
      map((account) =>
        account ? account.roles.some((role) => role.type === roleType) : false
      )
    );
  }

  private getSocket(): Socket {
    if (!this.socket) {
      this.socket = io(this.rootUrl);
    }
    return this.socket;
  }

  async requestNotificationPermission() {
    this.notificationPermission$.next(await Notification.requestPermission());
  }

  onEvent<T extends SocketEventType>(
    event: T,
    listener: (data: SocketEventMap[T]) => void
  ) {
    this.getSocket().on(event, listener as any);
  }

  offEvent<T extends SocketEventType>(
    event: T,
    listener: (data: SocketEventMap[T]) => void
  ) {
    this.getSocket().off(event, listener as any);
  }

  listenToNotifications() {
    if (this.newNoteHandler) {
      return;
    }
    this.newNoteHandler = (note: Note) => {
      this.notificationCount$.next(this.notificationCount$.value + 1);
      if (this.notificationPermission$.value === "granted") {
        new Notification("New notification!", {
          icon: logo,
        });
      }
    };
    this.onEvent(SocketEventType.NewNote, this.newNoteHandler);
  }

  clearNotifications() {
    this.notificationCount$.next(0);
  }

  /*
   * Registers a user
   */
  authRegister = async (
    params: APIParameters["authRegister"]
  ): Promise<APIResult["authRegister"]> => {
    const endpointCall = this.buildEndpointCall<"authRegister">({
      url: "/auth/register",
      method: "POST",
    });
    const result = await endpointCall(params);
    this.storeSessionToken(result);
    return result;
  };

  /*
   * Logs in to the account
   */
  authLogin = async (
    params: APIParameters["authLogin"]
  ): Promise<APIResult["authLogin"]> => {
    const endpointCall = this.buildEndpointCall<"authLogin">({
      url: "/auth/login",
      method: "POST",
    });
    const result = await endpointCall(params);
    this.storeSessionToken(result);
    return result;
  };

  /*
   * Logs out of the given account
   */
  authLogout = async (
    params: APIParameters["authLogout"]
  ): Promise<APIResult["authLogout"]> => {
    const endpointCall = this.buildEndpointCall<"authLogout">({
      url: "/auth/logout",
      method: "DELETE",
    });
    const result = await endpointCall(params);
    this.deleteSessionToken();
    return result;
  };

  /*
   * Gets accounts
   */
  accountGet = this.buildEndpointCall<"accountGet">({
    url: "/account",
    method: "GET",
  });

  /*
   * Updates an account
   */
  accountPut = this.buildEndpointCall<"accountPut">({
    url: "/account",
    method: "PUT",
  });

  accountDeviceTokenPost = this.buildEndpointCall<"accountDeviceTokenPost">({
    url: "/account/deviceToken",
    method: "POST",
  });

  /*
   * Gets information about the currently logged in user
   */
  me = async ({
    skipCache,
  }: {
    skipCache?: boolean;
  } = {}): Promise<APIResult["me"]> => {
    const cachedValue = this.account$.getValue();
    if (!skipCache && cachedValue) {
      return cachedValue;
    }
    const endpointCall = this.buildEndpointCall<"me">({
      url: "/account/me",
      method: "GET",
    });
    try {
      const mePromises = this.mePromises;
      if (mePromises === null) {
        this.mePromises = [];
      } else {
        return new Promise((r) => {
          mePromises.push(r);
        });
      }
      const result = await endpointCall({});
      this.account$.next(result);
      this.mePromises.forEach((r) => {
        r(result);
      });
      this.mePromises = null;
      return result;
    } catch (e) {
      if (e instanceof APIError && e.getCode() === APIErrorCode.InvalidToken) {
        this.deleteSessionToken();
      }
      throw e;
    }
  };

  /*
   * Gets information about the teams
   */
  teamGet = this.buildEndpointCall<"teamGet">({
    method: "GET",
    url: "/team",
  });

  teamPost = this.buildEndpointCall<"teamPost">({
    method: "POST",
    url: "/team",
  });

  teamPut = this.buildEndpointCall<"teamPut">({
    method: "PUT",
    url: "/team",
  });

  teamDelete = this.buildEndpointCall<"teamDelete">({
    method: "DELETE",
    url: "/team",
  });

  teamPointPost = this.buildEndpointCall<"teamPointPost">({
    method: "POST",
    url: "/team/point",
  });

  teamLeaderPost = this.buildEndpointCall<"teamLeaderPost">({
    method: "POST",
    url: "/team/leader",
  });

  teamLeaderDelete = this.buildEndpointCall<"teamLeaderDelete">({
    method: "DELETE",
    url: "/team/leader",
  });

  youthKidPropertiesGet = this.buildEndpointCall<"youthKidPropertiesGet">({
    method: "GET",
    url: "/youth-kid/properties",
  });

  youthKidGet = this.buildEndpointCall<"youthKidGet">({
    method: "GET",
    url: "/youth-kid",
  });

  youthKidPost = this.buildEndpointCall<"youthKidPost">({
    method: "POST",
    url: "/youth-kid",
  });

  youthKidPut = this.buildEndpointCall<"youthKidPut">({
    method: "PUT",
    url: "/youth-kid",
  });

  youthKidDelete = this.buildEndpointCall<"youthKidDelete">({
    method: "DELETE",
    url: "/youth-kid",
  });

  /*
   * Gets a given app config
   */
  appConfigGet = this.buildEndpointCall<"appConfigGet">({
    url: "/app-config",
    method: "GET",
  });

  /*
   * Runs an sql statement
   */
  devSql = this.buildEndpointCall<"devSql">({
    url: "/dev/sql",
    method: "POST",
  });

  /*
   * Notes get
   */
  noteGet = this.buildEndpointCall<"noteGet">({
    url: "/note",
    method: "GET",
  });

  activityGet = this.buildEndpointCall<"activityGet">({
    url: "/activity",
    method: "GET",
  });

  activityPost = this.buildEndpointCall<"activityPost">({
    url: "/activity",
    method: "POST",
  });

  activityDelete = this.buildEndpointCall<"activityDelete">({
    url: "/activity",
    method: "DELETE",
  });

  constructor(private rootUrl: string) {
    console.log(`Initialised API at ${rootUrl}`);
  }

  /*
   * Validate the current token
   */
  async validateToken(): Promise<boolean> {
    // No token so escape
    if (!this.getSessionToken()) {
      return false;
    }
    try {
      await this.me();
      return true;
    } catch (e) {
      if (e instanceof APIError && e.getCode() === APIErrorCode.InvalidToken) {
        return false;
      }
      throw e;
    }
  }

  /*
   * Store session token
   */
  private storeSessionToken(sessionToken: SessionToken) {
    window.localStorage.setItem("sessionToken", JSON.stringify(sessionToken));
    this.isLoggedIn$.next(true);
  }

  private deleteSessionToken() {
    window.localStorage.removeItem("sessionToken");
    this.isLoggedIn$.next(false);
  }

  /*
   * Gets a given account id
   */
  getAccountId(): number | undefined {
    return this.getSessionToken()?.accountId;
  }

  /*
   * Gets a session token
   */
  getSessionToken(): SessionToken | undefined {
    const sessionTokenJSON = window.localStorage.getItem("sessionToken");
    if (!sessionTokenJSON) {
      return undefined;
    }
    const sessionToken: SessionToken = JSON.parse(sessionTokenJSON);
    // Remove if expired
    if (Date.now() > sessionToken.expiry) {
      this.deleteSessionToken();
      return undefined;
    }
    return sessionToken;
  }

  /*
   * Factory method to make post endpoint call
   */
  private buildEndpointCall<T extends keyof APIParameters>({
    url,
    method,
  }: {
    url: string;
    method: "POST" | "GET" | "PUT" | "DELETE";
  }): APICall<T> {
    return async (params) => {
      let endpoint = `${this.rootUrl}${url}`;
      const fetchParams = {
        method,
        headers: {
          "Content-Type": "application/json",
          authorization: "",
        },
      };
      const token = this.getSessionToken()?.token;
      if (token) {
        fetchParams.headers.authorization = `token ${token}`;
      }
      if (method === "GET") {
        endpoint = endpoint + "?" + new URLSearchParams(params as any);
      } else {
        (fetchParams as any)["body"] = JSON.stringify(params);
      }

      const response = await fetch(endpoint, fetchParams);
      const result = await response.json();
      if (response.status !== 200) {
        throw new APIError({ code: result.code });
      }
      return result;
    };
  }
}
